import { useState, useEffect } from "react";

/**
 * Interactive Soroban abacus.
 * Each rod: 1 heaven bead (value 5, above the reckoning bar) + 4 earth beads
 * (value 1 each, below the bar). Beads pushed toward the bar are "active".
 * Click/tap a bead to toggle. Works with mouse and touch.
 */
export function SorobanAbacus({ rods = 5, value = 0, onChange, readOnly = false, decimals = 0 }) {
  // internal rod state derived from a numeric value
  const [state, setState] = useState(() => valueToState(value, rods));

  useEffect(() => {
    setState(valueToState(value, rods));
  }, [value, rods]);

  const emit = (next) => {
    setState(next);
    if (onChange) onChange(stateToValue(next, decimals));
  };

  const toggleHeaven = (r) => {
    if (readOnly) return;
    const next = state.map((rod, i) => (i === r ? { ...rod, heaven: !rod.heaven } : rod));
    emit(next);
  };

  // click earth bead index k (1..4 from the bar going down)
  const setEarth = (r, k) => {
    if (readOnly) return;
    const cur = state[r].earth;
    const newVal = cur >= k ? k - 1 : k;
    const next = state.map((rod, i) => (i === r ? { ...rod, earth: newVal } : rod));
    emit(next);
  };

  const decimalStart = rods - decimals; // rods to the right of this index are decimals

  return (
    <div className="inline-block select-none rounded-2xl bg-gradient-to-b from-amber-100 to-amber-200 p-4 shadow-inner border-4 border-amber-800/70" data-testid="soroban">
      <div className="flex gap-1.5">
        {state.map((rod, r) => {
          const isDecimal = decimals > 0 && r >= decimalStart;
          return (
            <div key={r} className="relative flex flex-col items-center" data-testid={`rod-${r}`}>
              {/* frame rod line */}
              <div className="absolute top-2 bottom-2 w-1.5 bg-amber-900/40 rounded-full" />
              {/* heaven zone */}
              <div className="relative z-10 flex h-16 w-10 flex-col items-center justify-start pt-1">
                <Bead
                  active={rod.heaven}
                  color={isDecimal ? "#DC2626" : "#1E3A8A"}
                  onClick={() => toggleHeaven(r)}
                  style={{ transform: rod.heaven ? "translateY(26px)" : "translateY(0)" }}
                  testid={`rod-${r}-heaven`}
                />
              </div>
              {/* reckoning bar */}
              <div className="relative z-20 h-2 w-full rounded-sm bg-amber-900" />
              {/* earth zone */}
              <div className="relative z-10 flex h-28 w-10 flex-col items-center justify-end pb-1">
                {[1, 2, 3, 4].map((k) => {
                  // earth bead k is active (pushed up) when rod.earth >= (5-k)?? map positions:
                  // we render top->bottom as positions 1..4; active beads sit just under the bar
                  const posFromBar = k; // 1 nearest bar
                  const activeCount = rod.earth;
                  const isActive = posFromBar <= activeCount;
                  return (
                    <Bead
                      key={k}
                      active={isActive}
                      color={isDecimal ? "#DC2626" : "#1E3A8A"}
                      onClick={() => setEarth(r, posFromBar)}
                      style={{ transform: isActive ? `translateY(-${(activeCount - posFromBar) * 0}px)` : "translateY(0)" }}
                      shift={isActive ? -8 : 8}
                      testid={`rod-${r}-earth-${k}`}
                    />
                  );
                })}
              </div>
              {/* place label */}
              <div className={`mt-1 text-[10px] font-bold ${isDecimal ? "text-red-600" : "text-amber-900/70"}`}>
                {placeLabel(rods - 1 - r, decimals)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Bead({ active, color, onClick, style, shift = 0, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="my-[1px] h-6 w-9 rounded-[50%] shadow-md transition-transform duration-150 active:scale-95"
      style={{
        background: `radial-gradient(circle at 35% 30%, ${lighten(color)}, ${color})`,
        transform: `translateY(${shift}px)`,
        ...style,
      }}
      aria-pressed={active}
    />
  );
}

function placeLabel(power, decimals) {
  const p = power - decimals;
  if (p === 0) return "1";
  if (p === 1) return "10";
  if (p === 2) return "100";
  if (p === 3) return "1k";
  if (p === 4) return "10k";
  if (p === 5) return "100k";
  if (p < 0) return "0." + "0".repeat(-p - 1) + "1";
  return `1e${p}`;
}

function lighten(hex) {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) + 70, g = ((n >> 8) & 255) + 70, b = (n & 255) + 70;
  r = Math.min(255, r); g = Math.min(255, g); b = Math.min(255, b);
  return `rgb(${r},${g},${b})`;
}

export function valueToState(value, rods) {
  const v = Math.max(0, Math.round(Number(value) || 0));
  const digits = String(v).padStart(rods, "0").slice(-rods).split("").map(Number);
  return digits.map((d) => ({ heaven: d >= 5, earth: d % 5 }));
}

export function stateToValue(state, decimals = 0) {
  let s = "";
  for (const rod of state) s += String((rod.heaven ? 5 : 0) + rod.earth);
  const intVal = parseInt(s, 10) || 0;
  if (decimals > 0) return intVal / Math.pow(10, decimals);
  return intVal;
}
